import { MersennePrimeUtils } from './mersenne-prime-utils.js';
import { Result } from './result.js';

export class ShuffleUtils {
  /**
   * Bijective endofunction on the set of integers modulo m.
   * @see {@link findUnshuffledInteger} for the inverse function.
   *
   * @param {bigint} source the integer to shuffle.
   * @param {bigint} modulo the modulo value.
   * @param {bigint} offset the additive offset.
   * @return {Result} a different integer from the interval [0, modulo) or an error message.
   */
  public static findShuffledInteger(
      source: bigint,
      modulo: bigint,
      offset: bigint,
  ): Result<bigint, string> {
    if(modulo <= BigInt(0)) {
      return Result.error('The modulo value must be positive.');
    }

    if(source < BigInt(0) || source >= modulo) {
      return Result.error('The source value must be in the interval [0, modulo).');
    }

    while(offset < BigInt(0)) {
      offset += modulo;
    }

    const x = source;

    const m = modulo;
    const aResult = MersennePrimeUtils.findPrimeLargerThan(m);
    if(aResult.isError) {
      return aResult;
    }

    const a = aResult.value;
    const b = offset;

    const result = (a * x + b) % m;

    return Result.ok(result);
  }

  /**
   * Inverse of {@link findShuffledInteger}.
   * @param {bigint} shuffled the integer to unshuffle.
   * @param {bigint} modulo the modulo value.
   * @param {bigint} offset the additive offset.
   *
   * @return {Result} the original integer or an error message.
   */
  public static findUnshuffledInteger(
      shuffled: bigint,
      modulo: bigint,
      offset: bigint,
  ): Result<bigint, string> {
    if(modulo <= BigInt(0)) {
      return Result.error('The modulo value must be positive.');
    }

    if(shuffled < BigInt(0) || shuffled >= modulo) {
      return Result.error('The shuffled value must be in the interval [0, modulo).');
    }

    while(offset < BigInt(0)) {
      offset += modulo;
    }

    const y = shuffled;

    const m = modulo;
    const aResult = MersennePrimeUtils.findPrimeLargerThan(m);
    if(aResult.isError) {
      return aResult;
    }

    const a = aResult.value;
    const b = offset;

    const aInverse = this.findModularMultiplicativeInverse(a, m);
    const bInverse = this.findModularAdditiveInverse(b, m);
    const result = (aInverse * (y + bInverse)) % m;

    return Result.ok(result);
  }

  /**
   * Guaranteed to work if {@link a} and {@link m} are coprime.
   * Otherwise we may end up with a division by zero exception.
   *
   * @param {bigint} a the number to find the modular multiplicative inverse of.
   * @param {bigint} m the modulo value.
   *
   * @return {bigint} the modular multiplicative inverse of a modulo m.
   */
  private static findModularMultiplicativeInverse(a: bigint, m: bigint): bigint {
    let m0 = m;
    let a0 = a;
    let t0 = BigInt(0);
    let t = BigInt(1);

    while(a0 > BigInt(1)) {
      const q = a0 / m0;
      let temp = m0;
      m0 = a0 % m0;
      a0 = temp;
      temp = t0;
      t0 = t - q * t0;
      t = temp;
    }

    while(t < BigInt(0)) {
      t += m;
    }

    return t;
  }

  private static findModularAdditiveInverse(a: bigint, m: bigint): bigint {
    let t = -a;
    while(t < BigInt(0)) {
      t += m;
    }

    return t;
  }
}
